import {
  faCheck,
  faChartLine,
  faUsers,
  faClock,
  faDollarSign,
  faBoxes,
  faHistory,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { REGISTRATION_STEPS, useRegistrationContext } from './registrationContext';
import { ProfitRoverPrimaryButton } from '../forms/ProfitRoverButtons';
import { ProfitRoverLogoIcon } from '../generic/ProfitRoverLogos';
import ProfitRoverSpinner from '../spinner/ProfitRoverSpinner';
import { useSubscriptionTrialLengths } from '../../data-access/query/stripe';
import './product-description.scss';

/**
 * Developer Note: This component may need to say something different based on what
 * kind of pricing scheme is "on offer" according to the server. For now, we will only
 * indicate pricing info for "regular free trial" pricing. In the future, we may require
 * additional logic.
 */
const CostPerLocation = ({ monthlyPrice, coupon, per }) => {
  const couponDiscount = coupon?.percent_off ?? 0;
  const couponDuration = coupon?.duration_in_months ?? 0;

  const pricePerUnitStr = (monthlyPrice / 100).toFixed(2);
  const couponPrice = pricePerUnitStr * (1 - couponDiscount / 100);

  const [dollars = '0'] = pricePerUnitStr.split('.');
  const [couponDollars = '0'] = couponPrice.toFixed(2).split('.');

  return (
    <div className="cost-per-location">
      <div className="price-row">
        {coupon && (
          <div className="dollars strikethrough-price">
            <span>$</span>
            <span>{dollars ?? '0'}</span>
          </div>
        )}
        <div className="dollars">
          <span>$</span>
          <span>{coupon ? couponDollars : dollars}</span>
        </div>
        <div className="per-text">/ {per} Per Location</div>
      </div>
      {coupon && (
        <div className="coupon-badge">
          {couponDiscount}% off for first {couponDuration} months
        </div>
      )}
    </div>
  );
};

const FeatureItem = ({ icon, title }) => (
  <div className="feature-item">
    <div className="feature-icon">
      <FontAwesomeIcon icon={icon} />
    </div>
    <span>{title}</span>
  </div>
);

const EXAMPLE_INDUSTRIES = [
  'Restaurants & Cafés',
  'Retail Stores',
  'Recreation & Entertainment',
  'Personal Services',
  'And many more...',
];

const PricingPanel = () => {
  const { setStep, annualPlan = {}, pricesFetching, coupon, couponFetching } = useRegistrationContext();
  const isFirstFreePricing = annualPlan?.tiers_mode != null;

  const { data: trialLengths = {} } = useSubscriptionTrialLengths();
  const { trialLength } = trialLengths;

  const goToNextStep = () => setStep(REGISTRATION_STEPS.REQUEST_CODE);

  if (pricesFetching || couponFetching) {
    return (
      <div className="pricing-loading">
        <ProfitRoverSpinner />
      </div>
    );
  }

  if (!isFirstFreePricing) {
    const unitPrice = annualPlan?.unit_amount ?? 0;
    const monthlyPrice = unitPrice / 12;

    return (
      <div className="pricing-content">
        <ProfitRoverLogoIcon className="form-logo" />
        <h2 className="pricing-headline">Start Your Free Trial</h2>

        <div className="pricing-box">
          <div className="pricing-label">Then just</div>
          <CostPerLocation monthlyPrice={monthlyPrice} coupon={coupon} per="Month" />
          <div className="pricing-sublabel">billed annually</div>
        </div>

        <ProfitRoverPrimaryButton className="cta-button" onClick={goToNextStep}>
          Start My {trialLength}-Day Free Trial
        </ProfitRoverPrimaryButton>

        <p className="no-card-text">No credit card required</p>
      </div>
    );
  }

  const [, nonFreeTier = {}] = annualPlan?.tiers ?? [];
  const { unit_amount: unitPrice = 0 } = nonFreeTier;
  const monthlyPrice = unitPrice / 12;

  return (
    <div className="pricing-content">
      <ProfitRoverLogoIcon className="form-logo" />
      <h2 className="pricing-headline">Start For Free</h2>

      <div className="pricing-box">
        <div className="free-location-badge">1 Free Location</div>
        <div className="pricing-divider">then</div>
        <CostPerLocation monthlyPrice={monthlyPrice} coupon={coupon} per="Month" />
        <div className="pricing-sublabel">per additional location, billed annually</div>
      </div>

      <ProfitRoverPrimaryButton className="cta-button" onClick={goToNextStep}>
        Get Started Free
      </ProfitRoverPrimaryButton>

      <p className="no-card-text">No credit card required</p>
    </div>
  );
};

const ProductDescription = () => {
  return (
    <div className="d-flex flex-column align-items-center">
      <div className="product-description-redesign">
        <div className="product-layout">
          {/* Left panel - Value proposition */}
          <div className="value-panel">
            <h1 className="main-headline">Data-driven insights to grow your profits</h1>
            <p className="sub-headline">
              Join thousands of businesses using AI-powered recommendations to optimize their operations.
            </p>

            <div className="features-list">
              <FeatureItem icon={faDollarSign} title="Smart Pricing Recommendations" />
              <FeatureItem icon={faUsers} title="Staffing Optimization" />
              <FeatureItem icon={faClock} title="Hours of Operation Insights" />
              <FeatureItem icon={faChartLine} title="Demand Forecasting" />
              <FeatureItem icon={faBoxes} title="Inventory Forecasting" />
              <FeatureItem icon={faHistory} title="Comprehensive Performance Analysis" />
            </div>

            <div className="industries-section">
              <p className="industries-label">Works great for businesses like:</p>
              <div className="industries-list">
                {EXAMPLE_INDUSTRIES.map(industry => (
                  <span key={industry} className="industry-tag">
                    <FontAwesomeIcon icon={faCheck} className="tag-icon" />
                    {industry}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Right panel - Pricing */}
          <div className="pricing-panel">
            <PricingPanel />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDescription;
